<p-confirmDialog #cd>
    <ng-template pTemplate="headless" let-message>
        <div class="flex flex-col align-items-center p-5 surface-overlay border-round w-[30rem]">
            <span class="font-bold text-2xl block mb-2 mt-4">
                {{ message.header }}
            </span>

            <p class="mb-0" [innerHTML]="message.message"></p>

            <div class="flex justify-end items-end float-left gap-2 mt-4">
                <app-button variant="outlined" severity="danger" [icon]="message.rejectIcon || 'pi pi-times'"
                    [label]="message.rejectLabel || 'No'" (clicked)="handleConfirmReject(message)" />

                <app-button variant="outlined" severity="success" [icon]="message.acceptIcon || 'pi pi-check'"
                    [label]="message.acceptLabel || 'Yes'" (clicked)="handleConfirmAccept(message)" />
            </div>
        </div>
    </ng-template>
</p-confirmDialog>

<p-dialog [(visible)]="displayCustomDialog" [modal]="true" [closable]="true" [closeIcon]="'false'"
    [style]="{width: '30rem'}">
    <ng-template pTemplate="headless">
        <div class="flex flex-col px-4 pb-4 align-items-center surface-overlay border-round w-full">
            <span class="font-bold text-2xl block mb-2 mt-4">{{ dialogHeader }}</span>

            <p class="mb-0" [innerHTML]="dialogMessage"></p>

            <div class="w-full mt-3" *ngIf="pendingAction === 'Reject'">
                <label for="rejectionComment" class="block font-medium mb-1">Comments<span
                        class="text-danger">*</span></label>
                <textarea id="rejectionComment" class="w-full p-2 border border-gray-300 rounded-base"
                    [(ngModel)]="rejectionComment" rows="3" placeholder="Enter rejection reason..."></textarea>
            </div>

            <div class="flex justify-end items-end gap-2 mt-4 w-full">
                <app-button label="Yes" (clicked)="onConfirmDialogAccept()" severity="success" variant="outlined"
                    icon="pi pi-check" />
                <app-button label="No" (clicked)="onConfirmDialogReject()" severity="danger" variant="outlined"
                    icon="pi pi-times" />
            </div>
        </div>
    </ng-template>
</p-dialog>

@if(loading){
<div class="flex justify-center items-center  h-screen">
    <div class="loader"></div>
</div>
}@else {

@if(permissions.restricted){
<!-- Cannot view contract -->
<div>
    <div class="flex flex-col items-center justify-center h-screen">
        <div class="flex flex-col items-center gap-4">
            <img [src]="'images/access_denied.png' | assetUrl" alt="404">
            <h1 class="text-2xl font-semibold text-center">You do not have permission to view this report</h1>
            <p class="text-gray-500 text-center">Please contact the owner of the report for access</p>
            <div class="mt-4">
                <app-button label="Login" variant="filled" severity="theme" (clicked)="navigateToLogin()"
                    icon="pi pi-sign-in">
                </app-button>
            </div>
        </div>
    </div>
</div>
}@else {
<section class="aspect-auto h-screen flex flex-col overflow-hidden min-h-0">
    <div class="flex bg-white overflow-x-auto gap-y-2 items-center p-2 drop-shadow border-b relative z-[50]">
        <div class="flex items-center min-w-0 max-w-[50%]" #left>
            @if(!isAnonymousUser){
            <app-button [icon]="SVG.GO_BACK" [iconSource]="'svg'" [size]="'default'" variant="text" class="text-sm"
                (clicked)="goBack()">
            </app-button>
            }
            <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2">
                    <span #editableContent scrollReset class="max-w-full text-md break-words line-clamp-2"
                        [attr.contenteditable]="isOwner || (isSameOrganization && permissions.canEdit)"
                        (keydown)="$event.key === 'Enter' ? blurSpan($event) : null" (blur)="updateReportName($event)">
                        {{analysisReport?.name}}
                    </span>
                    <div class="flex items-center">
                        @if(externalDocument){
                        <img src="/icons/external.svg" alt="external-icon"
                            [pTooltip]="isSameOrganization ? 'Shared Externally' : 'Owned Externally'" />
                        }
                    </div>
                </div>
            </div>
        </div>

        <div #right class="flex gap-2 items-center ml-auto flex-shrink-0">
            <app-credits-overview variant="chip"></app-credits-overview>
            <div class="relative">
                <app-popover #statusMenu appendTo="body" [width]="'12rem'" class="p-0 m-0" [overlayClickable]="false">
                    <ng-template #trigger>
                        @if(isSameOrganization) {
                        <app-button variant="outlined" severity="contrast" step-id="step1" [loading]="loadingStatuses"
                            class="border-none rounded-lg bg-blue-50" icon="pi pi-chevron-down" [iconPosition]="'right'"
                            [iconSize]="'sm'"
                            [label]="selectedStatus ? selectedStatus.name + (!selectedStatus.active ? ' Deleted' : '') : 'Select Status'" />
                        }
                    </ng-template>

                    <ng-template #body>
                        <div class="max-h-[25rem] overflow-y-auto p-2 min-w-[200px]" (click)="$event.stopPropagation()">
                            @for(item of statusMenuItems; track item.label) {
                            <div class="px-4 py-2 cursor-pointer hover:bg-gray-100 rounded transition-colors"
                                [ngClass]="item['class']" (click)="onStatusItemClick(item, statusMenu)">
                                {{ item.label }}
                                <span *ngIf="!item.active" class="text-danger">Deleted</span>
                            </div>
                            }
                        </div>
                    </ng-template>
                </app-popover>
            </div>

            <div class="flex gap-3 justify-center items-center">
                @if(analysisReport?.project){
                <app-button [icon]="SVG.PROJECT_FOLDER" [iconSource]="'svg'" [size]="'default'" variant="outlined"
                    [pTooltip]="analysisReport?.project?.name" tooltipPosition="top" class="text-sm"
                    [disabled]="loading">
                </app-button>
                }
            </div>

            @if(this.currentRole === CONTRACT_USER_ROLE.OWNER || this.isEditor){
            @if(checkDueDatePassed(analysisReport?.dueDate ?? null)){
            <app-button variant="outlined" [icon]="SVG.DUE_DATE_PASSED" [iconSource]="'svg'" [size]="'default'"
                pTooltip="Due Date Passed" tooltipPosition="top" class="text-sm" step-id="step4">
            </app-button>
            } @else {
            <div class="flex items-center gap-2 relative" step-id="step4">
                <div class="absolute opacity-0 pointer-events-none w-0 h-0 overflow-hidden">
                    <app-datepicker #dueDatePicker [(value)]="selectedDueDate" [config]="{
                            dateFormat: 'dd/mm/yy',
                            minDate: currentDate,
                            maxDate: projectDueDate || undefined,
                            showIcon: false,
                            keepInvalid: true,
                            showButtonBar: true,
                            appendTo: 'body'
                        }" (onSelect)="handleDueDateSelect($event)" (onClear)="handleDueDateClear()">
                        <ng-template pTemplate="date" let-date>
                            <span [class.selected-past-date]="isSelectedPastDate(date)"
                                [class.past-date]="date.day < currentDate.getDate()">
                                {{date.day}}
                            </span>
                        </ng-template>
                    </app-datepicker>
                </div>
                <app-button variant="outlined" [icon]="SVG.CALENDER" [iconSource]="'svg'" [size]="'default'"
                    pTooltip="Due Date" tooltipPosition="top" class="text-sm"
                    (clicked)="dueDatePicker.datepickerComponent.toggle($event)" step-id="step4">
                </app-button>
            </div>
            }
            }

            @if(this.isOwner || (isSameOrganization && this.permissions.canEdit)){
            <div step-id="step5">
                @if(analysisReport){
                <app-share-report [analysisReport]="analysisReport" [projectId]="projectId"
                    [workspace]="currentUser?.currentWorkspace?.toString() || null" />
                }
            </div>
            }
            <div class="flex items-center gap-2">
                @if(!isAnonymousUser) {
                <div class="flex items-center justify-center w-9 h-9 bg-white border border-gray-200 rounded-lg">
                    <app-notifications class="cursor-pointer"></app-notifications>
                </div>
                }
                <!-- Desktop view: Show icons -->
                <div class="hidden xl:flex gap-4">
                    <app-menu #support [items]="supportMenuItems">
                        <div class="flex items-center gap-1 p-[9px] border border-gray-200 rounded-lg"
                            [ngClass]="{'cursor-not-allowed opacity-50': isRecording(), 'cursor-pointer': !isRecording(), 'bg-primary': support.isVisible}"
                            (click)="support.toggle($event)" [pTooltip]="'Support'" tooltipPosition="left">
                            <app-icon [type]="TOP_BAR_SVG.SUPPORT" [source]="'svg'" [size]="'sm'" />
                            <app-icon [type]="support.isVisible ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
                                [source]="'primeng'" [size]="'sm'" />
                        </div>
                    </app-menu>
                </div>
                <app-menu #info [items]="infoMenuItems">
                    <div class="flex items-center gap-1 cursor-pointer p-[9px] border border-gray-200 rounded-lg"
                        [ngClass]="{'bg-primary': info.isVisible}" (click)="info.toggle($event)"
                        [pTooltip]="'Assistance'" tooltipPosition="left">
                        <app-icon [type]="TOP_BAR_SVG.ASSISTANCE" [source]="'svg'" [size]="'sm'" />
                        <app-icon [type]="info.isVisible ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
                            [source]="'primeng'" [size]="'sm'" />
                    </div>
                </app-menu>

                <!-- Mobile view: Show hamburger menu -->
                <div class="xl:hidden relative">
                    <div class="text-xl cursor-pointer" (click)="toggleMenu()">
                        <i class="pi pi-ellipsis-v arrow-right"></i>
                    </div>
                    <div *ngIf="menuOpen"
                        class="absolute right-0 bg-white flex flex-col gap-y-1 shadow-md rounded-base p-2 w-40 border border-gray-200">
                        @for( item of items;track item){
                        <p (click)="handleClick(item)"
                            class="cursor-pointer flex items-center gap-2 p-2 hover:bg-gray-100 hover:rounded">
                            <i class={{item.icon}}></i> {{item.label}}
                        </p>
                        }
                    </div>
                </div>
            </div>
        </div>
    </div>



    <!-- 2-column resizable layout: left dashboard (70%) + right tabs panel (30%) -->
    <div class="relative flex-1 min-h-0 overflow-hidden">
        <!-- Collapse / expand toggle button, anchored on the splitter gutter -->
        <button *ngIf="!isGutterResizing"
            class="geo-panel-toggle-button"
            [style.left.%]="getRightButtonPosition()"
            [style.top]="'50%'"
            [style.transform]="'translate(-50%, -50%)'"
            [pTooltip]="rightPanelCollapsed ? 'Expand panel' : 'Collapse panel'"
            tooltipPosition="left"
            (click)="toggleRightPanel()">
            <i [class]="rightPanelCollapsed ? 'pi pi-chevron-left' : 'pi pi-chevron-right'"></i>
        </button>

        <p-splitter
            [panelSizes]="getPanelSizes()"
            [minSizes]="getMinSizes()"
            layout="horizontal"
            styleClass="h-full w-full border-none"
            [style]="{ height: '100%' }"
            (onResizeStart)="onResizeStart($event)"
            (onResize)="onPanelResizing($event)"
            (onResizeEnd)="onPanelResize($event)">
            <!-- Left: dashboard tabs with Setup button -->
            <ng-template pTemplate>
                <div class="h-full w-full overflow-hidden bg-white px-1 py-2">
                    <!-- Overlay captures mouse events so iframe/pdf doesn't steal drag -->
                    @if (isGutterResizing) {
                    <div class="absolute inset-0 z-50 cursor-col-resize" style="pointer-events: all;"></div>
                    }
                    <ng-container *ngTemplateOutlet="dashboardSide"></ng-container>
                </div>
            </ng-template>
            <!-- Right: Reports / Exports / Comments tabs -->
            <ng-template pTemplate>
                <div class="h-full w-full overflow-hidden bg-white" [ngClass]="{'p-1': rightPanelCollapsed}">
                    @if (isGutterResizing) {
                    <div class="absolute inset-0 z-50 cursor-col-resize" style="pointer-events: all;"></div>
                    }
                    @if (!rightPanelCollapsed) {
                    <ng-container *ngTemplateOutlet="tabsSide"></ng-container>
                    } @else {
                    <!-- Collapsed state: rotated label -->
                    <div class="h-full flex flex-col items-center justify-start pt-4">
                        <div class="writing-mode-vertical text-sm font-bold text-gray-600 transform rotate-180">
                            Reports
                        </div>
                        <div class="mt-4">
                            <i class="pi pi-file text-2xl text-gray-400"></i>
                        </div>
                    </div>
                    }
                </div>
            </ng-template>
        </p-splitter>
    </div>
</section>
}
}


<!-- Setup button rendered flush-right inside the dashboard tab bar via the rightActions slot -->
<ng-template #dashboardSetupButton>
    <app-button
        icon="settings"
        iconSource="lucide"
        label="Setup"
        [size]="'default'"
        variant="outlined"
        severity="contrast"
        pTooltip="Setup"
        tooltipPosition="top"
        (clicked)="geoReportDataService.setSetupMode(true)">
    </app-button>
</ng-template>

<ng-template #dashboardSide>
    <div class="h-full w-full flex flex-col overflow-hidden bg-white">
        @if (geoReportDataService.setupMode()) {
        <!-- Setup mode: back button + setup tabs replace the normal dashboard -->
        <div class="flex flex-col h-full w-full min-h-0 p-2">
            <div class="flex justify-between items-center gap-2 shrink-0 pb-2">
                <div class="flex justify-start items-center gap-2 cursor-pointer" (click)="onSetupBack()">
                    <app-icon [type]="'chevron-left'" [source]="'lucide'" [size]="'lg'"></app-icon>
                    <span>Dashboard</span>
                </div>
            </div>
            <app-tabs-panels [tabs]="setupTabsConfig" [activeTabValue]="setupActiveTabValue" variant="modern"
                (tabChanged)="setupActiveTabValue = $event.value" [scrollable]="false" panelBorder="default"
                tabPanelsClass="flex-1 min-h-0" class="flex-1 min-h-0">
            </app-tabs-panels>
        </div>
        } @else {
        <!-- Normal dashboard tabs with Setup button anchored to the right of the tab strip -->
        <app-tabs-panels
            [tabs]="dashboardTabsConfig"
            [activeTabValue]="dashboardActiveTabValue"
            [rightActions]="dashboardSetupButton"
            (tabChanged)="dashboardActiveTabValue = $event.value"
            [scrollable]="true"
            variant="modern"
            panelBorder="default"
            tabPanelsClass="flex-1 overflow-auto">
        </app-tabs-panels>
        }
    </div>
</ng-template>


<ng-template #tabsSide>
    <!-- Right panel: Reports / Exports / Comments tabs – flat underline style matching compare-report-view -->
    <div class="h-full w-full flex-1">
        <app-tabs-panels
            [tabs]="tabsConfig"
            [activeTabValue]="activeTabValue"
            [scrollable]="true"
            [customClass]="'h-full flex flex-col overflow-hidden'"
            [tabListClass]="'document-tabs border-b'"
            [tabPanelsClass]="'flex-1 overflow-hidden bg-white'"
            (tabChanged)="onTabChanged($event)"
            class="h-full flex flex-col">
        </app-tabs-panels>
    </div>
</ng-template>


<p-dialog header="Add Milestones" [(visible)]="milestonesDialog" appendTo="body" [modal]="true"
    [style]="{width: '70vw', height: '90vh'}">
    <ng-template pTemplate="content">
        <app-milestones #milestonesComponent [milestones]="milestones" (save)="onMilestoneSave($event)"
            (cancel)="onMilestoneCancel()">
        </app-milestones>
    </ng-template>
</p-dialog>

<p-dialog header="Raise a Support Ticket" [(visible)]="supportTicketDialog" [modal]="true" [closable]="true"
    [draggable]="false" [resizable]="false" class="support-ticket-dialog"
    [style]="{width: '40rem', height: '72rem', maxWidth: '90vw', maxHeight: '90vh', zIndex: '10001'}"
    [baseZIndex]="10001" appendTo="body" (onHide)="onSupportTicketDialogHide()">
    <app-support-ticket-form #supportTicketForm [isDialog]="true" (ticketSubmitted)="onSupportTicketSubmitted()">
    </app-support-ticket-form>
</p-dialog>

<app-capture-issue #captureIssue (captureCompleted)="onCaptureCompleted($event)"></app-capture-issue>

<app-share-access-dialog [user]="shareAccessData.user" [visible]="shareAccessData.visible"
    [orgLevel]="shareAccessData.orgLevel" [workspaceLevel]="shareAccessData.workspaceLevel"
    [resource]="shareAccessData.resource" [feature]="shareAccessData.feature"
    (visibleChange)="shareAccessData.visible = $event" [permissions]="shareAccessData.permissions"
    (userAccessChanged)="shareAccessData.onComplete($event)"></app-share-access-dialog>

<!-- Workspace Mismatch Dialog -->
<p-dialog [(visible)]="workspaceMismatchDialog" [modal]="true" [closable]="false" [closeOnEscape]="false"
    [style]="{width: '30rem'}">
    <ng-template pTemplate="headless">
        <div class="flex flex-col px-4 pb-4 align-items-center surface-overlay border-round w-full">
            <span class="font-bold text-2xl block mb-2 mt-4">You're in the Wrong Workspace</span>

            <p>
                This document is available in a different workspace. Please switch to the appropriate workspace to
                access it.
            </p>

            <div class="flex justify-end items-end gap-2 mt-4 w-full">
                <app-button label="Switch Workspace" (clicked)="switchToContractWorkspace()" severity="primary"
                    variant="filled" icon="pi pi-sign-out" />
            </div>
        </div>
    </ng-template>
</p-dialog>

@if (analysisId) {
<app-chat-bubble
    chatType="geotechnical"
    [chatConfig]="{ contractId: analysisId }" />
}
